import { useQuery } from '@tanstack/react-query';

import { HOTEL_OFFER_TYPES, OFFER_TYPE_BUNDLE_AND_SAVE } from '~/consts/offerTypes';

import OffersService from '~/services/OffersService';
import ReservationService from '~/services/ReservationService';
import VendorsService from '~/services/VendorsService';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface OfferBreadcrumbData {
  vendor: BreadcrumbItem | null;
  property: BreadcrumbItem | null;
  offer: BreadcrumbItem | null;
  isMultiVendor: boolean;
  isMultiProperty: boolean;
}

interface UseOfferBreadcrumbsParams {
  offerId: string | null;
  enabled?: boolean;
}

interface UseOfferBreadcrumbsResult {
  data: OfferBreadcrumbData | null;
  isLoading: boolean;
  error: Error | null;
}

async function fetchBreadcrumbData(offerId: string): Promise<OfferBreadcrumbData> {
  const offerResult = await OffersService.getOffer(offerId);
  const offer: App.Offer = offerResult.result;

  const isBundleAndSave = offer.type === OFFER_TYPE_BUNDLE_AND_SAVE;

  if (isBundleAndSave) {
    return {
      vendor: { label: 'Multiple Vendors' },
      property: { label: 'Multiple Properties' },
      offer: {
        label: offer.name || 'Offer',
        href: `/offers/${offer.id_salesforce_external}`,
      },
      isMultiVendor: true,
      isMultiProperty: true,
    };
  }

  const vendorId = offer.vendor_account_id;
  let vendorName = 'Vendor';
  let propertyId: string | null = null;
  let propertyName = 'Property';

  // Fetch vendor name
  if (vendorId) {
    try {
      const vendorResult = await VendorsService.getVendorById(vendorId);
      vendorName = vendorResult.result?.vendor_name || vendorResult.result?.name || 'Vendor';
    } catch {
      // Keep default vendorName
    }
  }

  // Get property ID from packages (for hotel offers)
  const isHotelOffer = HOTEL_OFFER_TYPES.includes(offer.type);
  if (isHotelOffer && offer.packages && offer.packages.length > 0) {
    const firstPackage = offer.packages[0];
    propertyId = firstPackage.fk_property_id || null;

    if (propertyId) {
      try {
        const propertyResult = await ReservationService.getProperty(propertyId);
        propertyName = propertyResult.result?.name || 'Property';
      } catch {
        // Keep default propertyName
      }
    }
  }

  return {
    vendor: vendorId
      ? {
          label: vendorName,
          href: `/vendors/${vendorId}`,
        }
      : null,
    property:
      vendorId && propertyId
        ? {
            label: propertyName,
            href: `/vendors/${vendorId}/properties/${propertyId}`,
          }
        : null,
    offer: {
      label: offer.name || 'Offer',
      href: `/offers/${offer.id_salesforce_external}`,
    },
    isMultiVendor: false,
    isMultiProperty: false,
  };
}

export default function useOfferBreadcrumbs({
  offerId,
  enabled = true,
}: UseOfferBreadcrumbsParams): UseOfferBreadcrumbsResult {
  const isEnabled = enabled && Boolean(offerId);

  const { data, isLoading, error } = useQuery<OfferBreadcrumbData>({
    queryKey: ['offer-breadcrumbs', offerId],
    queryFn: async () => {
      if (!offerId) {
        throw new Error('Offer ID is required');
      }
      return fetchBreadcrumbData(offerId);
    },
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - breadcrumb data doesn't change often
    retry: false,
  });

  return {
    data: data ?? null,
    isLoading,
    error: error as Error | null,
  };
}
